import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ROI_LINK_KIND_LABELS, type RoiDeletedProject } from '@wr/shared';
import { retention } from '../../../../../config/portal.json';
import { ApiError, errorMessage, get, post } from '../../api.js';
import { Icon } from '../../components/Icon.js';
import { useToast } from '../../components/Toast.js';
import { EmptyState, Notice } from '../../components/ui.js';
import { useSignedIn } from '../../session.js';
import { formatDay } from './RoiList.js';

interface Page {
  items: RoiDeletedProject[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Settings > Deleted ROI projects (admins, roi.restore): what was deleted
 * within the ROI trash period, newest first, each with Restore. Older ones
 * are purged by the daily job (brief section 11).
 */
export function DeletedProjects() {
  const { refresh } = useSignedIn();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') ?? '1') || 1;
  const [data, setData] = useState<Page | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    get<Page>(`/roi/deleted?page=${page}`)
      .then((res) => current && setData(res))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
  }, [page, refresh, reload]);

  async function restore(project: RoiDeletedProject) {
    setBusy(project.id);
    setError(null);
    try {
      await post(`/roi/deleted/${project.id}/restore`);
      toast(`Project restored: ${project.link.company}, ${project.scenarioName}`);
      setReload((n) => n + 1);
    } catch (err) {
      setError(
        err instanceof ApiError && err.body.reason === 'duplicate_scenario'
          ? 'A project in use already has this calculator, client and scenario name, so this one cannot come back. It stays here until it is removed for good.'
          : errorMessage(err),
      );
    } finally {
      setBusy(null);
    }
  }

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      <Link to="/settings" className="back">
        Settings
      </Link>
      <h1 className="page">Deleted ROI projects</h1>
      <p className="lede">
        Projects deleted in the last {retention.roiTrashDays} days. Restore puts one back in the ROI
        list, as it was; after {retention.roiTrashDays} days a project is removed for good.
      </p>
      {error && <Notice kind="error">{error}</Notice>}
      {data &&
        (data.items.length === 0 ? (
          <EmptyState title="No deleted project." />
        ) : (
          <ul className="list" aria-label="Deleted ROI projects">
            {data.items.map((project) => (
              <li key={project.id} className="row static">
                <span className="av lead" aria-hidden="true">
                  <Icon name="roi" />
                </span>
                <span className="tx">
                  <span className="t1">{project.link.company}</span>
                  <span className="t2">
                    {project.scenarioName}, by {project.createdBy.name ?? 'a former user'}
                  </span>
                  <span className="le-tags">
                    <span className="tag">
                      {project.link.label} ({ROI_LINK_KIND_LABELS[project.link.kind]})
                    </span>
                    <span className="tag">
                      Deleted {formatDay(project.deletedAt)}
                      {project.deletedBy ? ` by ${project.deletedBy.name ?? 'a former user'}` : ''}
                    </span>
                    <span className="tag">Removed for good {formatDay(project.purgeAt)}</span>
                  </span>
                </span>
                <span className="rt">
                  <button
                    type="button"
                    className="btn sm"
                    disabled={busy === project.id}
                    aria-label={`Restore ${project.link.company}, ${project.scenarioName}`}
                    onClick={() => void restore(project)}
                  >
                    Restore
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ))}
      {data && pages > 1 && (
        <nav className="pager" aria-label="Pages">
          <button
            type="button"
            className="btn sm"
            disabled={page <= 1}
            onClick={() => setParams({ page: String(page - 1) })}
          >
            Previous
          </button>
          <span>
            {page} / {pages}
          </span>
          <button
            type="button"
            className="btn sm"
            disabled={page >= pages}
            onClick={() => setParams({ page: String(page + 1) })}
          >
            Next
          </button>
        </nav>
      )}
    </>
  );
}
